import { FC, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import LoadingButton from "@mui/lab/LoadingButton";
import Typography from '@mui/material/Typography';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import "../../index.css"
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { clearState } from '../../store/passwordSlice';
import CustomTextField from '../../components/FormFields/CustomTextField';
import { forgotPassword } from '../../store/passwordThunk';
import { ApiStatus } from '../../utils/api.types';
import { IForgotPasswordFormInputs } from '../../utils/password.types';
import { Link } from '@mui/material';
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import '../SignIn/css2/style.css';

// Schema to validate forgot password form.
const schema = yup.object().shape({
    email: yup.string().email().required('Email is required.'),
});

interface LoginProps {
    setType: (k: any) => void;
}
/**
 * Component for Forgot Password.
 */
const ForgotPassword: FC<LoginProps> =({setType}) => {
    const methods = useForm<IForgotPasswordFormInputs>({
        resolver: yupResolver(schema),
    });

    const { status } = useAppSelector((state) => state.password);

    const dispatch = useAppDispatch();
    const navigate = useNavigate()

    /**
     * Method to handle forgot password form submit.
     */
    const onSubmit = (data: IForgotPasswordFormInputs) => {
        // Dispatch forgot password action with forgot password form data.
        dispatch(forgotPassword(data));
        window.location.reload();
    };

    /**
     * Clear state on reset password page load.
     */
    useEffect(() => {
        return () => {
            dispatch(clearState());
        };
    }, [dispatch]);

    /**
     * Update UI based on the state(Success or Error).
     */
    useEffect(() => {
        if (status === ApiStatus.FAILED) {
            dispatch(clearState());
        }

        // If reset password is successful then logout the user and then 
        // redirect the user to login page.
        if (status === ApiStatus.SUCCESS) {
            dispatch(clearState());

            navigate('/reset-password', { replace: true });
        }
    }, [status, dispatch, navigate]);

    return (
        <Box sx={{ width: '100%' }}>
            <FormProvider {...methods}>
                <Box
                    component="form"
                    onSubmit={methods.handleSubmit(onSubmit)}
                    noValidate
                    style={{ paddingLeft: '35px' }}
                >
                    <Typography
                        style={{ marginBottom: 12 }}
                        component={Link}
                        onClick={() => setType('0')}
                        variant="body1"
                        sx={{ textDecoration: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}
                        color="#aaa"
                    >
                        <ArrowBackIosIcon sx={{ fontSize: 14 }} />
                        Back to Login
                    </Typography>

                    <h3 className="mb-5">Forgot Password</h3>

                    <label>Email address</label>
                    <CustomTextField
                        label=""
                        placeholder="Enter email"
                        className="c-text"
                        sx={{ width: '100%', mt: 0.5, mb: 2 }}
                        InputProps={{
                            disableUnderline: true,
                        }}
                        size="small"
                        name="email"
                        type="email"
                        variant="standard"
                        required
                    />

                    <LoadingButton
                        loading={status === ApiStatus.LOADING}
                        className="c-button"
                        disableElevation
                        type="submit"
                        style={{ width: '60%' }}
                    >
                        Submit
                    </LoadingButton>
                </Box>
            </FormProvider>
        </Box>
    )
}

export default ForgotPassword;
